import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../lib/util';
import { BenefitSetRequest } from '@pxtra-client-lib-legacy/gen';

export const usePostBenefitSet = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      companyId,
      requestBody
    }: {
      companyId: string;
      requestBody: BenefitSetRequest;
    }) => {
      return await apiClient.benefits.benefitsBenefitSetsCreate({
        company: companyId ?? 'MISSING_ID',
        requestBody: requestBody
      });
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['benefitSets', variables.companyId] });
    },
    onError: (error) => {
      console.error('Error creating benefit set:', error);
    }
  });
};

export const usePostBenefitSetEmployees = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      setId,
      accountingPeriodMonth,
      accountingPeriodYear,
      requestBody
    }: {
      setId: string;
      accountingPeriodMonth: number;
      accountingPeriodYear: number;
      requestBody: {
        employees?: Array<string>;
        employee?: string;
        force_retract?: boolean;
      };
    }) => {
      return apiClient.benefits.benefitsBenefitSetsEmployeesCreate({
        setId: setId ?? 'MISSING_ID',
        accountingPeriodMonth: accountingPeriodMonth,
        accountingPeriodYear: accountingPeriodYear,
        requestBody: requestBody
      });
    },
    onSuccess: (data, variables) => {
      const needsConfirmation =
        !!data && 'confirmation_required' in data && data.confirmation_required === true;
      if (needsConfirmation) return;

      queryClient.invalidateQueries({ queryKey: ['benefitSets', variables.setId] });

      queryClient.invalidateQueries({ queryKey: ['employees'] });
      queryClient.invalidateQueries({ queryKey: ['employee'] });

      queryClient.removeQueries({ queryKey: ['nextMonthBenefitSet'] });
    }
  });
};

export const usePatchBenefitSet = () => {
  return useMutation({
    mutationFn: async ({
      benefitSetId,
      requestBody
    }: {
      benefitSetId: string;
      requestBody: BenefitSetRequest;
    }) => {
      return await apiClient.benefits.benefitsBenefitSetsPartialUpdate({
        id: benefitSetId,
        requestBody: requestBody
      });
    }
  });
};

export function useDeleteBenefitSet(options = {}) {
  return useMutation({
    mutationFn: (data: { id: string }) =>
      apiClient.benefits.benefitsBenefitSetsDestroy({
        id: data.id
      }),
    ...options
  });
}
